/**
 * SanctionListManager - Manages sanction list sources
 *
 * Super Admin can add/edit/view sanction list sources.
 * Shows version history and allows triggering manual updates.
 */

import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import complianceService from "../../services/compliance";
import ComplianceTaskSettings from "./ComplianceTaskSettings";

const isDev = process.env.NODE_ENV === "development";

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    padding: "1.5rem",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
    paddingBottom: "1rem",
    borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1e293b",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "0.875rem",
    color: "#64748b",
    margin: "0.25rem 0 0 0",
    fontWeight: 400,
  },
  addButton: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.625rem 1rem",
    backgroundColor: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  listGrid: {
    display: "grid",
    gap: "1rem",
  },
  card: {
    background: "#fff",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    borderRadius: "12px",
    padding: "1.25rem",
    transition: "all 0.2s ease",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "0.75rem",
  },
  cardTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#1e293b",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  cardMeta: {
    display: "flex",
    gap: "1rem",
    flexWrap: "wrap",
    fontSize: "0.75rem",
    color: "#64748b",
    marginTop: "0.5rem",
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  linkText: {
    fontSize: "0.8125rem",
    color: "#3b82f6",
    wordBreak: "break-all",
    marginTop: "0.5rem",
    display: "block",
  },
  versionBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "0.25rem 0.5rem",
    backgroundColor: "rgba(59, 130, 246, 0.1)",
    color: "#3b82f6",
    borderRadius: "4px",
    fontSize: "0.75rem",
    fontWeight: 500,
  },
  actions: {
    display: "flex",
    gap: "0.5rem",
  },
  actionBtn: {
    padding: "0.375rem 0.75rem",
    background: "transparent",
    border: "1px solid rgba(148, 163, 184, 0.3)",
    borderRadius: "6px",
    fontSize: "0.75rem",
    fontWeight: 500,
    color: "#475569",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  modal: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)",
  },
  modalContent: {
    background: "#fff",
    borderRadius: "16px",
    padding: "1.5rem",
    width: "100%",
    maxWidth: "500px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
  modalTitle: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: "#1e293b",
    margin: "0 0 1.25rem 0",
  },
  formGroup: {
    marginBottom: "1rem",
  },
  label: {
    display: "block",
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: "#475569",
    marginBottom: "0.375rem",
  },
  input: {
    width: "100%",
    padding: "0.625rem 0.875rem",
    border: "1px solid rgba(148, 163, 184, 0.3)",
    borderRadius: "8px",
    fontSize: "0.875rem",
    color: "#1e293b",
    outline: "none",
    transition: "border-color 0.15s ease",
    boxSizing: "border-box",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.75rem",
    marginTop: "1.5rem",
    paddingTop: "1rem",
    borderTop: "1px solid rgba(148, 163, 184, 0.2)",
  },
  cancelBtn: {
    padding: "0.625rem 1rem",
    background: "transparent",
    border: "1px solid rgba(148, 163, 184, 0.3)",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#475569",
    cursor: "pointer",
  },
  submitBtn: {
    padding: "0.625rem 1.25rem",
    backgroundColor: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: 500,
    cursor: "pointer",
  },
  loadingOverlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "3rem",
    color: "#64748b",
    fontSize: "0.875rem",
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem 1rem",
    color: "#64748b",
  },
  emptyIcon: {
    fontSize: "2.5rem",
    marginBottom: "0.75rem",
    opacity: 0.5,
  },
  error: {
    padding: "0.75rem 1rem",
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.2)",
    borderRadius: "8px",
    color: "#dc2626",
    fontSize: "0.8125rem",
    marginBottom: "1rem",
  },
  success: {
    padding: "0.75rem 1rem",
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    border: "1px solid rgba(34, 197, 94, 0.2)",
    borderRadius: "8px",
    color: "#16a34a",
    fontSize: "0.8125rem",
    marginBottom: "1rem",
  },
};

// =============================================================================
// SUBCOMPONENTS
// =============================================================================

const SanctionListCard = ({
  list,
  onEdit,
  onViewVersions,
  onSync,
  onVerify,
  onPreview,
  isSyncing,
  isVerifying,
  isViewOnly,
}) => {
  const latestVersion = list.versions?.reduce(
    (latest, current) =>
      !latest || current.versionNumber > latest.versionNumber
        ? current
        : latest,
    null
  );

  return (
    <div
      style={styles.card}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(59, 130, 246, 0.3)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(148, 163, 184, 0.2)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={styles.cardHeader}>
        <div>
          <h3 style={styles.cardTitle}>{list.name}</h3>
          <div style={styles.cardMeta}>
            <span style={styles.metaItem}>
              <svg
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 1v4l2.5 1.5" />
                <circle cx="6" cy="6" r="5" />
              </svg>
              {list.lastUpdated
                ? format(new Date(list.lastUpdated), "MMM d, yyyy HH:mm")
                : "Never updated"}
            </span>
            {latestVersion && (
              <span style={styles.versionBadge}>
                v{latestVersion.versionNumber}
              </span>
            )}
          </div>
          <a
            href={list.link}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.linkText}
          >
            {list.link}
          </a>
        </div>
        <div style={styles.actions}>
          {!isViewOnly && (
            <>
              <button
                style={{
                  ...styles.actionBtn,
                  ...(isSyncing && { opacity: 0.6, cursor: "not-allowed" }),
                }}
                onClick={() => onSync(list)}
                title="Sync from URL now"
                disabled={isSyncing}
              >
                {isSyncing ? "Syncing..." : "Sync Now"}
              </button>
              <button
                style={{
                  ...styles.actionBtn,
                  ...(isVerifying && { opacity: 0.6, cursor: "not-allowed" }),
                }}
                onClick={() => onVerify(list)}
                title="Verify S3 sync status"
                disabled={isVerifying}
              >
                {isVerifying ? "Verifying..." : "Verify"}
              </button>
            </>
          )}
          <button
            style={styles.actionBtn}
            onClick={() => onPreview(list)}
            title="Preview records"
          >
            Preview
          </button>
          {!isViewOnly && (
            <button
              style={styles.actionBtn}
              onClick={() => onViewVersions(list)}
              title="View version history"
            >
              History
            </button>
          )}
          {!isViewOnly && (
            <button
              style={styles.actionBtn}
              onClick={() => onEdit(list)}
              title="Edit sanction list"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const AddEditModal = ({
  isOpen,
  onClose,
  onSubmit,
  editingList,
  isSubmitting,
  error,
}) => {
  const [name, setName] = useState("");
  const [link, setLink] = useState("");

  useEffect(() => {
    if (editingList) {
      setName(editingList.name || "");
      setLink(editingList.link || "");
    } else {
      setName("");
      setLink("");
    }
  }, [editingList, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ name, link });
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <h2 style={styles.modalTitle}>
          {editingList ? "Edit Sanction List" : "Add Sanction List Source"}
        </h2>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Name</label>
            <input
              type="text"
              style={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., UN Security Council Consolidated List"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Source URL (XML or JSON)</label>
            <input
              type="url"
              style={styles.input}
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://... (supports .xml or .json files)"
              required
            />
            <div
              style={{
                fontSize: "0.75rem",
                color: "#64748b",
                marginTop: "0.25rem",
              }}
            >
              Supports XML and JSON formats (e.g., PEP lists, sanction lists)
            </div>
          </div>
          <div style={styles.modalActions}>
            <button
              type="button"
              style={styles.cancelBtn}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitBtn,
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Saving..."
                : editingList
                ? "Update"
                : "Add Source"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const VersionHistoryModal = ({ isOpen, onClose, list }) => {
  if (!isOpen || !list) return null;

  const versions = [...(list.versions || [])].sort(
    (a, b) => b.versionNumber - a.versionNumber
  );

  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: "600px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={styles.modalTitle}>Version History: {list.name}</h2>

        {versions.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📋</div>
            <p>No versions available yet</p>
          </div>
        ) : (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {versions.map((version) => (
              <div
                key={version.versionNumber}
                style={{
                  padding: "0.75rem",
                  borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={styles.versionBadge}>
                    v{version.versionNumber}
                  </span>
                  <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                    {version.updatedAt
                      ? format(new Date(version.updatedAt), "MMM d, yyyy HH:mm")
                      : "Unknown"}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: "0.5rem",
                    fontSize: "0.75rem",
                    color: "#64748b",
                  }}
                >
                  Updated by: {version.updatedBy || "Unknown"}
                </div>
                <div
                  style={{
                    marginTop: "0.25rem",
                    fontSize: "0.6875rem",
                    color: "#94a3b8",
                    fontFamily: "monospace",
                  }}
                >
                  Hash: {version.contentHash?.slice(0, 16)}...
                </div>
                {version.s3Key && (
                  <div
                    style={{
                      marginTop: "0.25rem",
                      fontSize: "0.6875rem",
                      color: "#3b82f6",
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                    }}
                    title="S3 Storage Key"
                  >
                    S3: {version.s3Key}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={styles.modalActions}>
          <button style={styles.cancelBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Parse XML string into records
 */
const parseXmlRecords = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");

    // Check for parsing errors
    const parseError = xmlDoc.querySelector("parsererror");
    if (parseError) {
      throw new Error("Invalid XML format");
    }

    const records = [];

    // Common XML structures for sanction lists
    // Try to find individual entries (varies by list format)
    const findEntries = (node, path = []) => {
      if (!node) return;

      // Look for common patterns: Individual, Entity, Designation, etc.
      const tagName = node.tagName?.toLowerCase() || "";
      const isEntry =
        tagName.includes("individual") ||
        tagName.includes("entity") ||
        tagName.includes("designation") ||
        tagName.includes("entry") ||
        tagName.includes("person");

      if (isEntry) {
        const record = extractRecordData(node);
        if (record && Object.keys(record).length > 0) {
          records.push(record);
        }
      }

      // Recursively search children
      Array.from(node.children || []).forEach((child) => {
        findEntries(child, [...path, tagName]);
      });
    };

    findEntries(xmlDoc.documentElement);

    // If no structured entries found, try to extract all text nodes with names
    if (records.length === 0) {
      const allNodes = xmlDoc.querySelectorAll("*");
      allNodes.forEach((node) => {
        const tagName = node.tagName?.toLowerCase() || "";
        if (tagName.includes("name") && node.textContent?.trim()) {
          records.push({ name: node.textContent.trim() });
        }
      });
    }

    return records;
  } catch (error) {
    return [];
  }
};

/**
 * Extract record data from XML node
 */
const extractRecordData = (node) => {
  const record = {};

  const extractField = (fieldName, node) => {
    const field = node.querySelector(fieldName);
    if (field && field.textContent) {
      return field.textContent.trim();
    }
    return null;
  };

  // Try common field names
  const name =
    extractField("name", node) ||
    extractField("firstName", node) + " " + extractField("lastName", node) ||
    extractField("fullName", node) ||
    node.textContent?.trim();

  if (name) record.name = name;

  // Extract other common fields
  [
    "country",
    "dateOfBirth",
    "nationality",
    "address",
    "alias",
    "designation",
  ].forEach((field) => {
    const value = extractField(field, node);
    if (value) record[field] = value;
  });

  return record;
};

/**
 * Parse JSON and extract records
 * Handles various JSON structures including CIA World Leaders format
 */
const parseJsonRecords = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);
    const records = [];

    // Helper to safely get string value
    const getStringValue = (value) => {
      if (value === null || value === undefined) return "";
      if (typeof value === "string") return value.trim();
      if (typeof value === "number" || typeof value === "boolean")
        return String(value);
      if (Array.isArray(value)) {
        return value.map(getStringValue).filter(Boolean).join(", ");
      }
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return "";
    };

    // Helper to flatten nested objects for display (with depth limit)
    const flattenObject = (obj, prefix = "", depth = 0) => {
      if (depth > 5) return {}; // Prevent infinite recursion
      const flattened = {};

      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (value === null || value === undefined) {
          flattened[newKey] = "";
        } else if (Array.isArray(value)) {
          if (value.length === 0) {
            flattened[newKey] = "";
          } else if (
            value.every((v) => typeof v === "string" || typeof v === "number")
          ) {
            flattened[newKey] = value.map(String).join(", ");
          } else {
            // For complex arrays, just show count or first item
            flattened[newKey] = `[${value.length} items]`;
          }
        } else if (typeof value === "object") {
          // Recursively flatten nested objects
          Object.assign(flattened, flattenObject(value, newKey, depth + 1));
        } else {
          flattened[newKey] = getStringValue(value);
        }
      });

      return flattened;
    };

    // Special handling for CIA World Leaders format: data.leaders.nodes
    const handleCIAFormat = (data) => {
      try {
        // Try multiple possible paths
        let nodes = null;
        if (
          data?.data?.leaders?.nodes &&
          Array.isArray(data.data.leaders.nodes)
        ) {
          nodes = data.data.leaders.nodes;
        } else if (data?.leaders?.nodes && Array.isArray(data.leaders.nodes)) {
          nodes = data.leaders.nodes;
        } else if (Array.isArray(data?.data?.leaders)) {
          nodes = data.data.leaders;
        } else if (Array.isArray(data?.leaders)) {
          nodes = data.leaders;
        } else if (Array.isArray(data?.nodes)) {
          nodes = data.nodes;
        }

        if (Array.isArray(nodes) && nodes.length > 0) {
          nodes.forEach((node, nodeIndex) => {
            const country = node.country || "";
            const leaders = node.leaders || [];

            if (Array.isArray(leaders) && leaders.length > 0) {
              leaders.forEach((leader, leaderIndex) => {
                if (leader && typeof leader === "object") {
                  const name = leader.name;
                  // Handle both string and potentially other types
                  const nameStr =
                    typeof name === "string"
                      ? name.trim()
                      : name
                      ? String(name).trim()
                      : "";

                  if (nameStr) {
                    const record = {
                      name: nameStr,
                      country: country || "Unknown",
                    };

                    // Add any other fields from the leader object
                    Object.keys(leader).forEach((key) => {
                      if (
                        key !== "name" &&
                        leader[key] !== undefined &&
                        leader[key] !== null
                      ) {
                        record[key] = getStringValue(leader[key]);
                      }
                    });

                    records.push(record);
                    if (records.length <= 3) {
                    }
                  } else {
                  }
                } else {
                }
              });
            } else {
            }
          });

          if (records.length > 0) {
            return true;
          } else {
          }
        } else {
          if (data) {
            if (data.data) {
              if (data.data.leaders) {
              }
            }
          }
        }
      } catch (err) {
        console.error("[handleCIAFormat] Error:", err);
      }
      return false;
    };

    // Try CIA format first
    const ciaFormatWorked = handleCIAFormat(data);

    if (!ciaFormatWorked) {
      // Generic recursive search for records with names
      const findRecords = (obj, context = {}, depth = 0) => {
        if (depth > 15 || !obj || typeof obj !== "object") return; // Prevent infinite recursion

        if (Array.isArray(obj)) {
          obj.forEach((item, index) => {
            if (item && typeof item === "object") {
              // Check if this item has a name field
              if (
                item.name &&
                typeof item.name === "string" &&
                item.name.trim()
              ) {
                const record = {
                  name: item.name.trim(),
                  ...context,
                };

                // Add all other fields from the item
                Object.keys(item).forEach((key) => {
                  if (
                    key !== "name" &&
                    item[key] !== undefined &&
                    item[key] !== null
                  ) {
                    const value = item[key];
                    if (
                      typeof value === "string" ||
                      typeof value === "number" ||
                      typeof value === "boolean"
                    ) {
                      record[key] = String(value);
                    } else if (
                      Array.isArray(value) &&
                      value.length > 0 &&
                      value.every((v) => typeof v === "string")
                    ) {
                      record[key] = value.join(", ");
                    } else if (typeof value === "object") {
                      // Flatten nested objects
                      const flattened = flattenObject(value, key);
                      Object.assign(record, flattened);
                    }
                  }
                });

                records.push(record);
                if (records.length <= 3) {
                }
              } else {
                // Continue searching, preserving context
                const newContext = { ...context };
                if (item.country) newContext.country = item.country;
                findRecords(item, newContext, depth + 1);
              }
            }
          });
        } else {
          Object.entries(obj).forEach(([key, value]) => {
            const newContext = { ...context };
            if (key === "country" && typeof value === "string") {
              newContext.country = value;
            }
            findRecords(value, newContext, depth + 1);
          });
        }
      };

      findRecords(data);
    }

    // Deduplicate records based on name and country
    const seen = new Set();
    const uniqueRecords = records.filter((record) => {
      const key = `${record.name?.toLowerCase() || ""}_${
        record.country?.toLowerCase() || ""
      }`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (uniqueRecords.length > 0) {
    }

    return uniqueRecords;
  } catch (error) {
    console.error("[parseJsonRecords] Full error:", error);
    return [];
  }
};

/**
 * Preview Modal - Display and search sanction list records
 */
const PreviewModal = ({ isOpen, onClose, list }) => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [format, setFormat] = useState(null);

  useEffect(() => {
    if (isOpen && list) {
      loadPreviewData();
    } else {
      // Reset state when modal closes
      setRecords([]);
      setFilteredRecords([]);
      setSearchQuery("");
      setError(null);
      setFormat(null);
    }
  }, [isOpen, list]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = records.filter((record) => {
        return Object.values(record).some((value) => {
          if (typeof value === "string") {
            return value.toLowerCase().includes(query);
          }
          return false;
        });
      });
      setFilteredRecords(filtered);
    } else {
      setFilteredRecords(records);
    }
  }, [searchQuery, records]);

  const loadPreviewData = async () => {
    const listId = list._id || list.id;
    if (!listId) {
      setError("Invalid sanction list");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get latest version with signed URL
      const response = await complianceService.getLatestSanctionVersion(listId);
      const data = response?.data;

      if (!data || !data.latestVersion) {
        setError("No version data available");
        return;
      }

      const signedUrl = data.signedUrl;
      const versionFormat = data.latestVersion.format || "xml";
      setFormat(versionFormat);

      if (!signedUrl) {
        setError("No download URL available");
        return;
      }

      // Fetch the actual content
      const contentResponse = await fetch(signedUrl);
      if (!contentResponse.ok) {
        throw new Error(`Failed to fetch content: ${contentResponse.status}`);
      }

      const content = await contentResponse.text();

      // Validate content
      if (!content || content.trim().length === 0) {
        throw new Error("Content is empty");
      }

      // Auto-detect format from content (override stored format if wrong)
      const trimmedContent = content.trim();
      let detectedFormat = versionFormat;

      if (trimmedContent.startsWith("{") || trimmedContent.startsWith("[")) {
        // Try to parse as JSON to confirm
        try {
          JSON.parse(trimmedContent);
          detectedFormat = "json";
        } catch {
          // Not valid JSON, keep original format
        }
      } else if (trimmedContent.startsWith("<")) {
        detectedFormat = "xml";
      }

      if (detectedFormat !== versionFormat) {
        setFormat(detectedFormat);
      }

      // Parse based on detected format
      let parsedRecords = [];
      try {
        if (detectedFormat === "json") {
          parsedRecords = parseJsonRecords(content);
          if (parsedRecords.length === 0) {
          }
        } else {
          parsedRecords = parseXmlRecords(content);
          if (parsedRecords.length === 0) {
            // If XML parsing fails and content looks like JSON, try JSON as fallback
            if (
              trimmedContent.startsWith("{") ||
              trimmedContent.startsWith("[")
            ) {
              try {
                parsedRecords = parseJsonRecords(content);
                if (parsedRecords.length > 0) {
                  detectedFormat = "json";
                  setFormat("json");
                }
              } catch (jsonError) {}
            }
          }
        }
      } catch (parseError) {
        console.error("[PreviewModal] Parse error:", parseError);
        // Try alternative format as fallback
        if (
          detectedFormat === "xml" &&
          (trimmedContent.startsWith("{") || trimmedContent.startsWith("["))
        ) {
          try {
            parsedRecords = parseJsonRecords(content);
            detectedFormat = "json";
            setFormat("json");
          } catch (jsonError) {
            throw new Error(
              `Failed to parse as ${versionFormat.toUpperCase()} or JSON: ${
                parseError.message
              }`
            );
          }
        } else {
          throw new Error(
            `Failed to parse ${detectedFormat.toUpperCase()}: ${
              parseError.message
            }`
          );
        }
      }

      if (parsedRecords.length === 0) {
        // Show a more helpful error with debugging info
        const errorMsg = `No records found in the file. 
        
Debug info:
- Format: ${versionFormat}
- Content length: ${content.length} characters
- Content preview: ${content.substring(0, 200)}...

Please check the browser console for detailed parsing logs.`;
        setError(errorMsg);
      } else {
        setRecords(parsedRecords);
        setFilteredRecords(parsedRecords);
      }
    } catch (err) {
      console.error("[PreviewModal] Failed to load preview:", err);
      const errorMessage = err.message || "Failed to load preview data";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !list) return null;

  const displayRecords = filteredRecords;
  const recordCount = records.length;
  const filteredCount = displayRecords.length;

  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{
          ...styles.modalContent,
          maxWidth: "90vw",
          maxHeight: "90vh",
          width: "1200px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <h2 style={styles.modalTitle}>
            Preview: {list.name}
            {format && (
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  color: "#64748b",
                  marginLeft: "0.5rem",
                }}
              >
                ({format.toUpperCase()})
              </span>
            )}
          </h2>
          <button
            style={{
              ...styles.cancelBtn,
              padding: "0.5rem",
              minWidth: "auto",
            }}
            onClick={onClose}
            title="Close"
          >
            ✕
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <div style={styles.loadingOverlay}>
            <span>Loading records...</span>
          </div>
        ) : (
          <>
            <div
              style={{
                marginBottom: "1rem",
                display: "flex",
                gap: "1rem",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1, position: "relative" }}>
                <input
                  type="text"
                  style={{
                    ...styles.input,
                    paddingLeft: "2.5rem",
                  }}
                  placeholder="Search records..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <svg
                  width="16"
                  height="16"
                  style={{
                    position: "absolute",
                    left: "0.75rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    pointerEvents: "none",
                  }}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </div>
              <div
                style={{
                  fontSize: "0.8125rem",
                  color: "#64748b",
                  whiteSpace: "nowrap",
                }}
              >
                {filteredCount !== recordCount ? (
                  <>
                    Showing {filteredCount} of {recordCount} records
                  </>
                ) : (
                  <>
                    {recordCount} record{recordCount !== 1 ? "s" : ""}
                  </>
                )}
              </div>
            </div>

            {displayRecords.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>📋</div>
                <p>
                  {searchQuery
                    ? "No records match your search"
                    : "No records found"}
                </p>
              </div>
            ) : (
              <div
                style={{
                  maxHeight: "60vh",
                  overflowY: "auto",
                  border: "1px solid rgba(148, 163, 184, 0.2)",
                  borderRadius: "8px",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.8125rem",
                  }}
                >
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#f8fafc",
                      zIndex: 10,
                    }}
                  >
                    <tr
                      style={{
                        borderBottom: "2px solid rgba(148, 163, 184, 0.2)",
                      }}
                    >
                      {Object.keys(displayRecords[0] || {}).map((key) => (
                        <th
                          key={key}
                          style={{
                            padding: "0.75rem",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#475569",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRecords.map((record, index) => (
                      <tr
                        key={index}
                        style={{
                          borderBottom: "1px solid rgba(148, 163, 184, 0.1)",
                          transition: "background-color 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "rgba(59, 130, 246, 0.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {Object.keys(displayRecords[0] || {}).map((key) => (
                          <td
                            key={key}
                            style={{
                              padding: "0.75rem",
                              color: "#1e293b",
                              wordBreak: "break-word",
                            }}
                          >
                            {record[key] || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <div style={styles.modalActions}>
          <button style={styles.cancelBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const SanctionListManager = ({ isSuperAdmin, isViewOnly }) => {
  const [sanctionLists, setSanctionLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Version history modal
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [selectedListForVersions, setSelectedListForVersions] = useState(null);

  // Preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedListForPreview, setSelectedListForPreview] = useState(null);

  // Sync state (tracks which list is syncing)
  const [syncingListId, setSyncingListId] = useState(null);

  // Verification state
  const [verifyingListId, setVerifyingListId] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);

  const fetchSanctionLists = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await complianceService.listSanctionLists();
      const data = response?.data?.items || response?.data || [];

      setSanctionLists(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[SanctionListManager] Failed to fetch:", err);
      setError("Failed to load sanction lists. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSanctionLists();
  }, [fetchSanctionLists]);

  const handleAddNew = () => {
    setEditingList(null);
    setModalError(null);
    setShowAddModal(true);
  };

  const handleEdit = (list) => {
    setEditingList(list);
    setModalError(null);
    setShowAddModal(true);
  };

  const handlePreview = async (list) => {
    const listId = list._id || list.id;
    if (!listId) {
      setSelectedListForPreview(list);
      setShowPreviewModal(true);
      return;
    }

    try {
      // Fetch full record with versions
      const response = await complianceService.getSanctionList(listId);
      const fullList = response?.data || list;
      setSelectedListForPreview(fullList);
      setShowPreviewModal(true);
    } catch (err) {
      console.error("[SanctionListManager] Failed to fetch full list:", err);
      // Fallback to using the list we have
      setSelectedListForPreview(list);
      setShowPreviewModal(true);
    }
  };

  const handleViewVersions = async (list) => {
    const listId = list._id || list.id;
    if (!listId) {
      setSelectedListForVersions(list);
      setShowVersionsModal(true);
      return;
    }

    try {
      // Fetch full record with versions
      const response = await complianceService.getSanctionList(listId);
      const fullList = response?.data || list;
      setSelectedListForVersions(fullList);
      setShowVersionsModal(true);
    } catch (err) {
      console.error("[SanctionListManager] Failed to fetch full list:", err);
      // Fallback to using the list we have (may not have versions)
      setSelectedListForVersions(list);
      setShowVersionsModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingList(null);
    setModalError(null);
  };

  const handleSubmit = async ({ name, link }) => {
    try {
      setIsSubmitting(true);
      setModalError(null);
      setSuccess(null);

      if (editingList) {
        await complianceService.updateSanctionList(editingList._id, {
          name,
          link,
        });
        setSuccess("Sanction list updated successfully");
      } else {
        await complianceService.createSanctionList({ name, link });
        setSuccess(
          "Sanction list added successfully. Initial fetch may take a moment."
        );
      }

      handleCloseModal();
      await fetchSanctionLists();

      // Clear success message after delay
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error("[SanctionListManager] Submit failed:", err);
      const message =
        err?.response?.data?.message || err?.message || "Operation failed";
      setModalError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSync = async (list) => {
    const listId = list._id || list.id;
    if (!listId) return;

    try {
      setSyncingListId(listId);
      setError(null);
      setSuccess(null);

      const syncResponse = await complianceService.syncSanctionList(listId);

      if (isDev) {
        const syncedList = syncResponse?.data?.list;
        if (syncedList?.versions) {
          const latestVersion = syncedList.versions.reduce(
            (latest, current) =>
              !latest || current.versionNumber > latest.versionNumber
                ? current
                : latest,
            null
          );
        }
      }

      setSuccess(`Successfully synced "${list.name}" from source URL`);
      await fetchSanctionLists();

      // Clear success message after delay
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error("[SanctionListManager] Sync failed:", err);
      const message =
        err?.response?.data?.message || err?.message || "Sync failed";
      setError(`Failed to sync "${list.name}": ${message}`);
    } finally {
      setSyncingListId(null);
    }
  };

  const handleVerify = async (list) => {
    const listId = list._id || list.id;
    if (!listId) return;

    try {
      setVerifyingListId(listId);
      setError(null);
      setVerificationResult(null);

      const response = await complianceService.verifySanctionList(listId, {
        verifyS3: true,
      });

      const result = response?.data;
      setVerificationResult(result);

      if (result?.verified) {
        setSuccess(
          `✓ Verified: "${list.name}" is synced to S3 (${
            result.versionCount
          } version${result.versionCount !== 1 ? "s" : ""})`
        );
      } else {
        setError(
          `⚠ Verification: ${
            result?.message || "Sanction list not fully synced"
          }`
        );
      }

      // Clear messages after delay
      setTimeout(() => {
        setSuccess(null);
        setError(null);
        setVerificationResult(null);
      }, 10000);
    } catch (err) {
      console.error("[SanctionListManager] Verification failed:", err);
      const message =
        err?.response?.data?.message || err?.message || "Verification failed";
      setError(`Failed to verify "${list.name}": ${message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setVerifyingListId(null);
    }
  };

  return (
    <div style={styles.container}>
      {!isViewOnly && <ComplianceTaskSettings orgAdminId={isSuperAdmin?.id} />}

      <div className="mt-4" style={styles.header}>
        <div>
          <h1 style={styles.title}>Sanction List Sources</h1>
          <p style={styles.subtitle}>
            Manage external sanction list sources for compliance screening
          </p>
        </div>
        {!isViewOnly && (
          <button
            style={styles.addButton}
            onClick={handleAddNew}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#1e293b")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#0f172a")
            }
          >
            <svg
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M8 3v10M3 8h10" />
            </svg>
            Add Source
          </button>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {verificationResult && (
        <div
          style={{
            ...styles.card,
            marginBottom: "1rem",
            backgroundColor: verificationResult.verified
              ? "rgba(34, 197, 94, 0.05)"
              : "rgba(239, 68, 68, 0.05)",
            borderColor: verificationResult.verified
              ? "rgba(34, 197, 94, 0.3)"
              : "rgba(239, 68, 68, 0.3)",
          }}
        >
          <h4
            style={{
              margin: "0 0 0.75rem 0",
              fontSize: "0.9375rem",
              fontWeight: 600,
            }}
          >
            Verification Result: {verificationResult.name}
          </h4>
          <div style={{ fontSize: "0.8125rem", lineHeight: "1.6" }}>
            <div style={{ marginBottom: "0.5rem" }}>
              <strong>Status:</strong>{" "}
              <span
                style={{
                  color: verificationResult.verified ? "#16a34a" : "#dc2626",
                  fontWeight: 500,
                }}
              >
                {verificationResult.verified ? "✓ Verified" : "⚠ Not Verified"}
              </span>
            </div>
            <div style={{ marginBottom: "0.5rem" }}>
              <strong>Message:</strong> {verificationResult.message}
            </div>
            {verificationResult.latestVersion && (
              <div style={{ marginBottom: "0.5rem" }}>
                <strong>Latest Version:</strong> v
                {verificationResult.latestVersion.versionNumber}
                <br />
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontFamily: "monospace",
                    color: "#64748b",
                  }}
                >
                  S3 Key: {verificationResult.latestVersion.s3Key}
                </span>
              </div>
            )}
            {verificationResult.s3Verification && (
              <div style={{ marginBottom: "0.5rem" }}>
                <strong>S3 Status:</strong>{" "}
                {verificationResult.s3Verification.exists ? (
                  <span style={{ color: "#16a34a" }}>✓ File exists</span>
                ) : (
                  <span style={{ color: "#dc2626" }}>✗ File not found</span>
                )}
                {verificationResult.s3Verification.size && (
                  <>
                    <br />
                    <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                      Size:{" "}
                      {(
                        verificationResult.s3Verification.size /
                        1024 /
                        1024
                      ).toFixed(2)}{" "}
                      MB
                    </span>
                  </>
                )}
              </div>
            )}
            <div
              style={{
                fontSize: "0.75rem",
                color: "#64748b",
                marginTop: "0.5rem",
              }}
            >
              Versions: {verificationResult.versionCount} | Last Updated:{" "}
              {verificationResult.lastUpdated
                ? format(
                    new Date(verificationResult.lastUpdated),
                    "MMM d, yyyy HH:mm"
                  )
                : "Never"}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={styles.loadingOverlay}>
          <span>Loading sanction lists...</span>
        </div>
      ) : sanctionLists.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🛡️</div>
          <p>No sanction list sources configured</p>
          <p style={{ fontSize: "0.8125rem", marginTop: "0.5rem" }}>
            Add your first sanction list source to begin compliance screening
          </p>
        </div>
      ) : (
        <div style={styles.listGrid}>
          {sanctionLists.map((list) => (
            <SanctionListCard
              key={list._id}
              list={list}
              onEdit={handleEdit}
              onViewVersions={handleViewVersions}
              onPreview={handlePreview}
              onSync={handleSync}
              onVerify={handleVerify}
              isSyncing={syncingListId === (list._id || list.id)}
              isVerifying={verifyingListId === (list._id || list.id)}
              isViewOnly={isViewOnly}
            />
          ))}
        </div>
      )}

      <AddEditModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        editingList={editingList}
        isSubmitting={isSubmitting}
        error={modalError}
      />

      <VersionHistoryModal
        isOpen={showVersionsModal}
        onClose={() => {
          setShowVersionsModal(false);
          setSelectedListForVersions(null);
        }}
        list={selectedListForVersions}
      />

      <PreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedListForPreview(null);
        }}
        list={selectedListForPreview}
      />
    </div>
  );
};

export default SanctionListManager;
